import { Trash, Copy } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'
import { createStore, useStore } from 'zustand'
import {
  ComponentPropsWithoutRef,
  ComponentPropsWithRef,
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition
} from 'react'
import { Button, Checkbox } from '@/components/ui'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { TableRow } from '@/components/ui/table'
import { cn } from '@/lib/utils'

type TableActionPopoverProps = {
  defaultSelected?: string[]
}

interface TableActionPopoverState extends TableActionPopoverProps {
  selected: string[]
  setSelected: (selected: string[]) => void
  allSelected: boolean
  setAllSelected: (allSelected: boolean) => void
}

const createTableActionPopoverStore = (initialProps: TableActionPopoverProps) => {
  const DEFAULT_PROPS: TableActionPopoverProps = {
    defaultSelected: []
  }
  return createStore<TableActionPopoverState>()(set => ({
    ...DEFAULT_PROPS,
    ...initialProps,
    selected: initialProps.defaultSelected || [],
    setSelected: selected => set({ selected }),
    allSelected: false,
    setAllSelected: allSelected => set({ allSelected })
  }))
}

type TableActionPopoverStore = ReturnType<typeof createTableActionPopoverStore>

const TableActionPopoverContext = createContext<TableActionPopoverStore | null>(null)

const TableActionPopoverProvider = ({ children, ...props }: TableActionPopoverProps & { children: React.ReactNode }) => {
  const tableActionPopoverStore = useRef(createTableActionPopoverStore(props)).current
  return <TableActionPopoverContext value={tableActionPopoverStore}>{children}</TableActionPopoverContext>
}

function useTableActionPopoverStore<T>(selector: (state: TableActionPopoverState) => T): T {
  const store = use(TableActionPopoverContext)
  if (!store) throw new Error(`useTableActionPopoverStore must be used within a TableActionPopoverProvider`)
  return useStore(store, selector)
}

const TableActionPopover = ({
  children,
  onDuplicate,
  onDelete,
  maxItems
}: {
  children: React.ReactNode
  maxItems?: number
  onDuplicate?: () => void
  onDelete?: () => void
}) => {
  const { t } = useTranslation(`common`)
  const store = use(TableActionPopoverContext)
  if (!store) throw new Error(`TableActionPopover must be used within a TableActionPopoverProvider`)
  const [open, setOpen] = useState(false)
  const [, startTransition] = useTransition()
  const selectedItems = useStore(store, state => state.selected)
  const setSelected = useStore(store, state => state.setSelected)
  const setAllSelected = useStore(store, state => state.setAllSelected)
  useEffect(() => {
    const unsubscribe = store.subscribe(state => {
      startTransition(() => {
        setOpen(state.selected.length > 0)
        if (maxItems) {
          setAllSelected(state.selected.length >= maxItems)
        }
      })
    })
    return () => unsubscribe()
  }, [selectedItems, store, startTransition, maxItems, setAllSelected])

  const handleDuplicate = () => {
    if (onDuplicate) onDuplicate()
    setSelected([])
  }

  const handleDelete = () => {
    if (onDelete) onDelete()
    setSelected([])
  }

  // const dynamicOffset = useMemo(() => {
  //   const nodes = selectedItems.map((id) => document.getElementById(id))
  //   if (allSelected) return 0
  //   let lastNode = nodes[nodes.length - 1]
  //   let totalHeight = 0
  //   while (lastNode?.previousSibling) {
  //     if (!lastNode) break
  //     const { previousSibling } = lastNode as HTMLElement
  //     if (!previousSibling) break
  //     totalHeight += (previousSibling as HTMLElement).clientHeight
  //     lastNode = previousSibling as HTMLElement
  //   }
  //   return totalHeight
  // }, [selectedItems, allSelected])

  return (
    <Popover open={open}>
      <PopoverContent
        side='top'
        sticky='always'
        disableAnimation
        // sideOffset={-dynamicOffset}
        className='trasform w-fit translate-x-1/2'
      >
        <div className='flex items-center gap-2'>
          {onDuplicate ? (
            <Button size='sm' variant='ghost' onClick={handleDuplicate}>
              <Copy size={16} className='mr-2' weight='bold' />
              <span className='text-sm font-normal'>
                {t(`table.row_actions.duplicate`, {
                  count: selectedItems.length
                })}
              </span>
            </Button>
          ) : null}
          {onDelete ? (
            <Button size='sm' variant='ghost' className='text-red-400' onClick={handleDelete}>
              <Trash size={16} className='mr-2' weight='bold' />
              <span className='text-sm font-normal'>
                {t(`table.row_actions.delete`, {
                  count: selectedItems.length
                })}
              </span>
            </Button>
          ) : null}
        </div>
      </PopoverContent>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
    </Popover>
  )
}

const ActionableItemContext = createContext<{
  id: string
  isSelected: boolean
}>({
  id: ``,
  isSelected: false
})

const ActionableTableRow = ({
  ref,
  ...props
}: Omit<ComponentPropsWithRef<'tr'>, 'id'> & {
  id: string
}) => {
  const { id, className, ...rest } = props
  const selectedItems = useTableActionPopoverStore(state => state.selected)
  const isSelected = selectedItems.includes(id)
  const value = useMemo(() => ({ id, isSelected }), [id, isSelected])
  return (
    <ActionableItemContext.Provider value={value}>
      <TableRow
        ref={ref}
        {...rest}
        id={id}
        className={cn(
          `group`,
          {
            'bg-blue-10 hover:bg-blue-10': isSelected,
            'hover:bg-transparent': !isSelected
          },
          className
        )}
      />
    </ActionableItemContext.Provider>
  )
}

const ActionableCheckbox: React.FC<
  ComponentPropsWithoutRef<typeof Checkbox> & {
    ref?: React.Ref<HTMLButtonElement>
  }
> = ({ ref, className, ...props }) => {
  const itemCtx = use(ActionableItemContext)
  if (!itemCtx) throw new Error(`ActionableCheckbox must be used within an ActionableItemContext.Provider`)
  const setSelected = useTableActionPopoverStore(state => state.setSelected)
  const selectedItems = useTableActionPopoverStore(state => state.selected)
  const handleChange = useCallback(() => {
    const newSelected = selectedItems.includes(itemCtx.id) ? selectedItems.filter(id => id !== itemCtx.id) : [...selectedItems, itemCtx.id]
    setSelected(newSelected)
  }, [itemCtx.id, setSelected, selectedItems])
  return (
    <Checkbox
      id={itemCtx.id}
      checked={itemCtx.isSelected}
      onCheckedChange={handleChange}
      {...props}
      ref={ref}
      className={cn(
        `opacity-0 transition-opacity duration-300 group-hover:opacity-100`,
        {
          'opacity-100': itemCtx.isSelected
        },
        className
      )}
    />
  )
}

export {
  ActionableTableRow,
  TableActionPopover,
  TableActionPopoverContext,
  ActionableItemContext,
  createTableActionPopoverStore,
  useTableActionPopoverStore,
  TableActionPopoverProvider,
  ActionableCheckbox
}
